import { memo, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { brightenColor } from 'src/customizable-ui/dashboard/utils/brightenColor.js';
import PlaceHolder from 'src/_shared/assets/banner-bg.jpeg';
import dayjs from 'dayjs';

const AnnouncementThumbnailCard = memo(
	({
		card,
		isEditMode = true,
		isDragging = false,
		isResizing = false,
		isHovered = false,
		announcements = [],
	}) => {
		const cardData = card.data || {};
		const backgroundColor = cardData.backgroundColor || '#ffffff';
		const borderColor = cardData.borderColor || '#dddddd';
		const textColor = cardData.textColor || '#333333';
		const showBorder = cardData.showBorder !== false;
		const borderSize = cardData.borderSize || 1;

		const announcementPosition = cardData.announcementPosition || 1;

		const selectedAnnouncement = useMemo(() => {
			if (!announcements || announcements.length === 0) return null;
			const index = announcementPosition - 1;
			return announcements[index] || null;
		}, [announcements, announcementPosition]);

		const thumbnailUrl = useMemo(() => {
			if (!selectedAnnouncement?.thumbnail?.key) return PlaceHolder;
			const { bucket, key, region } = selectedAnnouncement.thumbnail;
			return `https://${bucket}.s3.${region}.amazonaws.com/${key}`;
		}, [selectedAnnouncement]);

		const announcementDate = useMemo(() => {
			if (!selectedAnnouncement?.dateCreated) return 'No Date';
			return dayjs(selectedAnnouncement.dateCreated).format('MMMM D, YYYY');
		}, [selectedAnnouncement]);

		const headline = selectedAnnouncement?.title || 'No Announcement Available';

		const announcementContent = useMemo(() => {
			if (!selectedAnnouncement?.content) return '';
			// Use DOMParser for safer HTML parsing
			const parser = new DOMParser();
			const doc = parser.parseFromString(
				selectedAnnouncement.content,
				'text/html'
			);
			return doc.body.textContent || doc.body.innerText || '';
		}, [selectedAnnouncement]);

		const maxContentLength = 120;

		const truncateContent = (text, maxLength) => {
			if (!text || text.length <= maxLength) return text;
			return text.substring(0, maxLength).trim() + '...';
		};

		const displayContent = truncateContent(
			announcementContent,
			maxContentLength
		);

		const getCardBorderStyle = () => {
			if (!showBorder && !isEditMode) return 'none';

			if (isDragging || isResizing) {
				return `${borderSize}px solid #74a9ffff`;
			}

			if (isEditMode && isHovered && !isDragging && !isResizing) {
				const brighterBorder = brightenColor(borderColor || '#dddddd', 30);
				return `${borderSize}px solid ${brighterBorder}`;
			}

			if (showBorder) {
				return `${borderSize}px solid ${borderColor}`;
			}

			return 'none';
		};

		const getCardShadow = () => {
			if (isDragging || isResizing) {
				return '0 25px 50px -12px rgba(0, 0, 0, 0.25)';
			}
			if (isEditMode && isHovered && !isDragging && !isResizing) {
				return '0 10px 15px -3px rgba(0, 0, 0, 0.1)';
			}
			return 'none';
		};

		return (
			<div
				className="w-full h-full flex flex-col relative transition-all duration-200"
				style={{
					background: backgroundColor,
					border: getCardBorderStyle(),
					boxShadow: getCardShadow(),
					transform: isDragging ? 'scale(1.05)' : 'scale(1)',
					borderRadius: '14px',
					overflow: 'hidden',
				}}
			>
				{/* Thumbnail Image */}
				<div
					style={{
						width: '100%',
						height: '60%',
						overflow: 'hidden',
						position: 'relative',
					}}
				>
					<img
						src={thumbnailUrl}
						alt="Announcement thumbnail"
						style={{
							width: '100%',
							height: '100%',
							objectFit: 'cover',
						}}
					/>
				</div>

				{/* Content Section */}
				<div
					style={{
						padding: '16px',
						display: 'flex',
						flexDirection: 'column',
						gap: '8px',
						flex: 1,
					}}
				>
					{/* Date */}
					<div
						style={{
							fontSize: '12px',
							color: textColor,
							opacity: 0.7,
							fontFamily: 'Nunito Sans, sans-serif',
							fontWeight: 500,
						}}
					>
						{announcementDate}
					</div>

					{/* Headline */}
					<h4
						style={{
							fontSize: '14px',
							fontWeight: 700,
							lineHeight: '1.4',
							margin: 0,
							color: textColor,
							fontFamily: 'Nunito Sans, sans-serif',
						}}
					>
						{headline}
					</h4>

					{/* Content */}
					{announcementContent && (
						<div
							style={{
								fontSize: '12px',
								lineHeight: '1.5',
								color: textColor,
								opacity: 0.8,
								fontFamily: 'Nunito Sans, sans-serif',
								marginTop: '4px',
							}}
						>
							{displayContent}
						</div>
					)}

					{/* Read More Button */}
					<Link
						to={`/announcements?announcementId=${selectedAnnouncement?.id}`}
						style={{
							fontSize: '12px',
							fontWeight: 600,
							color: '#007bff',
							textDecoration: 'none',
							marginTop: '8px',
							display: 'inline-block',
							transition: 'opacity 0.2s',
						}}
						onMouseEnter={(e) => (e.target.style.opacity = '0.7')}
						onMouseLeave={(e) => (e.target.style.opacity = '1')}
					>
						READ MORE →
					</Link>
				</div>
			</div>
		);
	}
);

AnnouncementThumbnailCard.displayName = 'AnnouncementThumbnailCard';

export default AnnouncementThumbnailCard;
